import React, { Component } from "react";
import { connect } from "react-redux";
import { PhoneIncoming, PhoneOutgoing, Search, Loader } from "lucide-react";
import placeholderImage from "../../../../Assets/svg/PlaceholderImage1.svg";
import { FetchAllCallsAction, SelectCallAction, FetchCallTranscriptAction } from "../../../../Actions/Calls";

class CallList extends Component {
  state = { search: "" };

  componentDidMount() {
    // Fetch all calls when component mounts
    this.props.FetchAllCallsAction();
  }

  handleCallClick = (call) => {
    // Update selected call in Redux
    this.props.SelectCallAction(call);
    // Fetch transcript for this call
    this.props.FetchCallTranscriptAction(call.call_id);
  };

  formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "Unknown";
    // Remove +1 prefix if exists and format nicely
    const cleaned = phoneNumber.replace('+1', '').replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  };

  renderCallItem = (call, index, type) => {
    const { selectedCall } = this.props;
    const isActive = selectedCall && selectedCall.call_id === call.call_id;

    // Determine display name based on call direction
    const displayNumber = type === "incoming" ? call.from_number : call.to_number;
    const displayName = this.formatPhoneNumber(displayNumber);
    const timeStr = this.formatTime(call.started_at);

    return (
      <button
        key={call.call_id}
        onClick={() => this.handleCallClick(call)}
        className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 transition-colors text-left w-full ${
          isActive ? "bg-blue-50" : "hover:bg-blue-50/40"
        }`}
      >
        <div className="relative shrink-0">
          <img
            src={placeholderImage}
            alt={displayName}
            className="h-10 w-10 rounded-full"
          />
          {isActive && (
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 border-2 border-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-baseline gap-1">
            <div
              className={`font-SFPro font-semibold text-sm truncate ${
                isActive ? "text-primary-blue" : "text-gray-900"
              }`}
            >
              {displayName}
            </div>
            <span className="font-inter text-[10px] text-gray-400 shrink-0">
              {timeStr}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            {type === "incoming" ? (
              <PhoneIncoming className="w-3 h-3 text-green-500 shrink-0" />
            ) : (
              <PhoneOutgoing className="w-3 h-3 text-primary-blue shrink-0" />
            )}
            <span className="font-inter text-xs text-gray-500 truncate">
              {type === "incoming" ? "Incoming call" : "Outgoing call"}
            </span>
            {call.status && (
              <span className={`font-inter text-[10px] ml-auto px-1.5 py-0.5 rounded ${
                call.status === 'completed' ? 'bg-green-100 text-green-700' :
                call.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                call.status === 'failed' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {call.status}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  render() {
    const { search } = this.state;
    const { allCalls, isLoadingCalls, callsError } = this.props;

    // Separate calls by direction
    const incomingCalls = allCalls.filter(call => call.direction === 'inbound');
    const outgoingCalls = allCalls.filter(call => call.direction === 'outbound');

    // Apply search filter
    const filteredIncoming = incomingCalls.filter((call) =>
      this.formatPhoneNumber(call.from_number).toLowerCase().includes(search.toLowerCase())
    );
    const filteredOutgoing = outgoingCalls.filter((call) =>
      this.formatPhoneNumber(call.to_number).toLowerCase().includes(search.toLowerCase())
    );

    const totalCalls = allCalls.length;

    return (
      <div className="flex flex-col w-72 shrink-0 h-full border border-blue-100 rounded-xl bg-white overflow-hidden">
        {/* Header */}
        <div
          className="flex justify-between items-center px-5 py-4 border-b border-blue-100 rounded-t-xl"
          style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}
        >
          <div className="flex items-center gap-2">
            <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
              Calls
            </p>
            <span className="inline-flex items-center bg-gray-50 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full border border-gray-100">
              {totalCalls} calls
            </span>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute top-1/2 -translate-y-1/2 left-3 w-4 h-4 text-gray-400 z-10" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              className="w-full py-2 pl-9 pr-3 text-sm font-inter bg-white border border-gray-200 focus:border-primary-blue outline-none rounded-lg transition-colors"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex flex-col overflow-y-auto flex-1">
          {isLoadingCalls ? (
            <div className="flex items-center justify-center py-10">
              <Loader className="w-6 h-6 text-primary-blue animate-spin" />
            </div>
          ) : callsError ? (
            <div className="px-4 py-6 text-center">
              <p className="text-red-500 text-sm">{callsError}</p>
              <button
                onClick={() => this.props.FetchAllCallsAction()}
                className="mt-2 text-primary-blue text-sm hover:underline"
              >
                Retry
              </button>
            </div>
          ) : totalCalls === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-gray-500 text-sm">No calls yet</p>
            </div>
          ) : (
            <>
              {/* Incoming section */}
              {filteredIncoming.length > 0 && (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <PhoneIncoming className="w-3.5 h-3.5 text-green-500" />
                    <span className="font-inter text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Incoming
                    </span>
                  </div>
                  {filteredIncoming.map((call, i) => this.renderCallItem(call, i, "incoming"))}
                </>
              )}

              {/* Outgoing section */}
              {filteredOutgoing.length > 0 && (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 mt-1">
                    <PhoneOutgoing className="w-3.5 h-3.5 text-primary-blue" />
                    <span className="font-inter text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Outgoing
                    </span>
                  </div>
                  {filteredOutgoing.map((call, i) => this.renderCallItem(call, i, "outgoing"))}
                </>
              )}

              {filteredIncoming.length === 0 && filteredOutgoing.length === 0 && search && (
                <div className="px-4 py-10 text-center">
                  <p className="text-gray-500 text-sm">No calls match your search</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  allCalls: state.calls.allCalls,
  isLoadingCalls: state.calls.isLoadingCalls,
  callsError: state.calls.callsError,
  selectedCall: state.calls.selectedCall,
});

const mapDispatchToProps = {
  FetchAllCallsAction,
  SelectCallAction,
  FetchCallTranscriptAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(CallList);
